import { useState, useMemo } from "react";
import {
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Tab,
    Tabs,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import {
    AddCircle,
    AltArrowUp,
    AltArrowDown,
    ChatRoundDots,
} from "@solar-icons/react/ssr";

import AiLogo from "@/ai/AiLogo";
import { WORKFLOW_REGISTRY } from "@/ai/workflowRegistry";
import { truncateString } from "@/utils";

const AGENT_TYPE_ORDER = [
    "intake",
    "brief",
    "bid",
    "bid_create",
    "timeline",
    "wrap_report",
    "router",
];

/**
 * AiSidebar — thread list (grouped by agent type), workflow picker, and sources.
 *
 * Props:
 *  - threads: Array<{ id, title, agent_type }> — all threads for the current scope
 *  - activeThreadId: number | null
 *  - availableWorkflows: string[] — which workflow types to show
 *  - sources: Array<{ name, url }> — files attached to the project
 *  - onSelectThread: (thread) => void
 *  - onNewChat: () => void — starts a router thread (New Chat)
 *  - onNewWorkflow: (agentType) => void — starts a new workflow thread
 *  - onAddSource: () => void — opens file uploader
 */
export default function AiSidebar({
    threads = [],
    activeThreadId,
    availableWorkflows = [],
    sources = [],
    onSelectThread,
    onNewChat,
    onNewWorkflow,
    onAddSource,
    defaultTab = 0,
}) {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [collapsedGroups, setCollapsedGroups] = useState({});

    const toggleGroup = (groupKey) => {
        setCollapsedGroups((prev) => ({
            ...prev,
            [groupKey]: !prev[groupKey],
        }));
    };

    const threadsByType = useMemo(() => {
        const grouped = {};
        for (const agentType of AGENT_TYPE_ORDER) {
            const matching = threads.filter((t) => t.agent_type === agentType);
            if (matching.length > 0 || availableWorkflows.includes(agentType)) {
                grouped[agentType] = matching;
            }
        }
        // Include ungrouped threads (null agent_type)
        const ungrouped = threads.filter((t) => !t.agent_type);
        if (ungrouped.length > 0) {
            grouped["general"] = ungrouped;
        }
        return grouped;
    }, [threads, availableWorkflows]);

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRight: `1px solid ${theme.palette.divider}`,
                background: theme.palette.background.default,
                overflow: "hidden",
            }}
        >
            {/* Header */}
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <AiLogo additionalStyles={{ width: 20, height: 20 }} />
                    <Typography fontWeight={600}>AI Assistant</Typography>
                </Box>

                <Button
                    startIcon={<AddCircle size={18} />}
                    variant="contained"
                    clickable
                    color="inherit"
                    onClick={onNewChat}
                    sx={{ width: "100%" }}
                >
                    New Thread
                </Button>
            </Box>

            {/* Tab Toggle: Threads | Workflows */}
            <Tabs
                value={activeTab}
                onChange={(_, val) => setActiveTab(val)}
                variant="fullWidth"
                scrollButtons="auto"
                sx={{
                    minHeight: 36,
                    "& .MuiTab-root": {
                        minHeight: 36,
                        textTransform: "none",
                        fontSize: 13,
                        py: 0.5,
                    },
                }}
            >
                <Tab label="Threads" />
                <Tab label="Workflows" />
            </Tabs>

            <Divider sx={{ opacity: 0.5 }} />

            {/* Scrollable content area */}
            <Box sx={{ flex: 1, overflow: "auto" }}>
                {activeTab === 0 && (
                    <ThreadList
                        threadsByType={threadsByType}
                        activeThreadId={activeThreadId}
                        collapsedGroups={collapsedGroups}
                        onToggleGroup={toggleGroup}
                        onSelectThread={onSelectThread}
                    />
                )}
                {activeTab === 1 && (
                    <WorkflowList
                        availableWorkflows={availableWorkflows}
                        onNewWorkflow={onNewWorkflow}
                    />
                )}
            </Box>

            {sources.length > 0 && (
                <>
                    <Divider sx={{ opacity: 0.5 }} />
                    {/* Sources (pinned bottom) */}
                    <SourcesSection
                        sources={sources}
                        onAddSource={onAddSource}
                    />
                </>
            )}
        </Box>
    );
}

/**
 * Grouped thread list with collapsible sections by agent type.
 */
function ThreadList({
    threadsByType,
    activeThreadId,
    collapsedGroups,
    onToggleGroup,
    onSelectThread,
}) {
    const theme = useTheme();

    return (
        <Box sx={{ py: 0.5 }}>
            {Object.entries(threadsByType).map(([agentType, typeThreads]) => {
                const config = WORKFLOW_REGISTRY[agentType];
                const label = config?.label || "General";
                const Icon =
                    WORKFLOW_REGISTRY[agentType]?.icon || ChatRoundDots;
                const isCollapsed = collapsedGroups[agentType];

                return (
                    <Box key={agentType}>
                        {/* Group header */}
                        <ListItemButton
                            onClick={() => onToggleGroup(agentType)}
                            sx={{ py: 0.5, px: 2, minHeight: 32 }}
                        >
                            <Box
                                sx={{
                                    mr: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                <Icon size={16} />
                            </Box>
                            <ListItemText
                                primary={
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        color="text.secondary"
                                    >
                                        {label} ({typeThreads.length})
                                    </Typography>
                                }
                            />
                            {isCollapsed ? (
                                <AltArrowDown size={16} />
                            ) : (
                                <AltArrowUp size={16} />
                            )}
                        </ListItemButton>

                        {/* Thread items */}
                        <Collapse in={!isCollapsed}>
                            <List disablePadding dense>
                                {typeThreads.map((thread) => (
                                    <Tooltip
                                        key={thread.id}
                                        title={truncateString(
                                            thread.title,
                                            120,
                                        )}
                                        placement="right"
                                        enterDelay={500}
                                    >
                                        <ListItem disablePadding>
                                            <ListItemButton
                                                selected={
                                                    activeThreadId === thread.id
                                                }
                                                onClick={() =>
                                                    onSelectThread(thread)
                                                }
                                                sx={{
                                                    pl: 4.5,
                                                    py: 0.25,
                                                    minHeight: 28,
                                                }}
                                            >
                                                <ListItemText
                                                    primary={truncateString(
                                                        thread.title,
                                                        30,
                                                    )}
                                                    primaryTypographyProps={{
                                                        fontSize: 13,
                                                        noWrap: true,
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    </Tooltip>
                                ))}
                            </List>
                        </Collapse>
                    </Box>
                );
            })}
        </Box>
    );
}

/**
 * Workflow picker — buttons to start new workflow threads.
 */
function WorkflowList({ availableWorkflows, onNewWorkflow }) {
    const theme = useTheme();

    return (
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {availableWorkflows.map((agentType) => {
                const config = WORKFLOW_REGISTRY[agentType];
                if (!config) return null;
                const Icon =
                    WORKFLOW_REGISTRY[agentType]?.icon || ChatRoundDots;

                return (
                    <Button
                        key={agentType}
                        startIcon={<Icon size={18} />}
                        variant="contained"
                        color="inherit"
                        fullWidth
                        onClick={() => onNewWorkflow(agentType)}
                    >
                        {config.label}
                    </Button>
                );
            })}
        </Box>
    );
}

/**
 * Sources section — pinned to the bottom of the sidebar.
 */
function SourcesSection({ sources = [], onAddSource }) {
    const theme = useTheme();

    return (
        <Box sx={{ px: 2, py: 1, maxHeight: "30%", overflow: "auto" }}>
            <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
            >
                Sources
            </Typography>

            {sources.length > 0 && (
                <List dense disablePadding sx={{ mb: 0.5 }}>
                    {sources.map((source, index) => (
                        <ListItem key={index} disablePadding>
                            <ListItemButton
                                component="a"
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ borderRadius: 1, py: 0.25, px: 0.5 }}
                            >
                                <ListItemText
                                    primary={source.name}
                                    primaryTypographyProps={{
                                        fontSize: 12,
                                        noWrap: true,
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            )}

            {/* <Chip
                variant="outlined"
                size="small"
                clickable
                onClick={onAddSource}
                label="Add source"
                icon={<AddCircle size={18} />}
                sx={{ width: "100%", justifyContent: "flex-start", fontSize: 12 }}
            /> */}
        </Box>
    );
}
